import { useMemo } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useStayDestinations } from "../api/stays-query";
import { useCitiesOptional } from "../../activities/hooks/use-cities";
import { StaySearchPill } from "./stay-search-pill";
import { parseChildAges } from "../lib/nights";

/**
 * The search pill on /category/stays.
 *
 * Search stays on the hub: the city goes in the path, the dates and party in
 * the query, and StaysHubGrid prices every LiteAPI property for them.
 */
export function StaysHubSearch({
  lang,
  citySlug,
  search,
}: {
  lang: string;
  citySlug?: string;
  search?: { checkin?: string; checkout?: string; adults?: number | string; childAges?: string | number };
}) {
  const value = {
    city: citySlug,
    checkin: search?.checkin,
    checkout: search?.checkout,
    adults: search?.adults != null && search.adults !== "" ? Number(search.adults) : 2,
    childAges: parseChildAges(search?.childAges),
  };
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: destinationsData } = useStayDestinations();
  const { data: citiesResponse } = useCitiesOptional();

  const cities = useMemo(() => {
    const art = new Map(
      ((citiesResponse?.data ?? []) as { slug: string; image?: string | null }[])
        .filter((c) => c.image)
        .map((c) => [c.slug, c.image as string]),
    );
    return (destinationsData?.destinations ?? [])
      .filter((d): d is typeof d & { slug: string } => !!d.slug && (d.propertyCount ?? 0) > 0)
      .map((d) => ({ slug: d.slug, name: d.name, image: art.get(d.slug) ?? d.heroImage ?? undefined }));
  }, [destinationsData, citiesResponse]);

  const hubCities = ((citiesResponse?.data ?? []) as { slug: string; name: string }[]).filter((c) => c.slug);

  return (
    <>
    {/* Every city hub as a plain link in the server HTML, as the city picker
        on the other hubs leaves them: the popup renders nothing until opened,
        and without these the Stays hub stops linking its own city pages. */}
    <nav aria-label={t("explore.cities_nav", { defaultValue: "Cities" })} className="sr-only">
      <ul>
        {hubCities.map((c) => (
          <li key={c.slug}>
            <Link to="/$lang/category/$category/$city" params={{ lang, category: "stays", city: c.slug }} tabIndex={-1}>
              {c.name}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
    <StaySearchPill
      cities={cities}
      value={value}
      onSearch={(next) => {
        // "All" now lists every LiteAPI property, so the search prices them
        // right here rather than sending the guest to the hotel list.
        const search = {
          ...(next.checkin && next.checkout
            ? {
                checkin: next.checkin,
                checkout: next.checkout,
                adults: next.adults,
                ...(next.childAges.length ? { childAges: next.childAges.join(",") } : {}),
              }
            : {}),
        };
        if (next.city) {
          void navigate({ to: "/$lang/category/$category/$city", params: { lang, category: "stays", city: next.city }, search: search as never, resetScroll: false });
        } else {
          void navigate({ to: "/$lang/category/$category", params: { lang, category: "stays" }, search: search as never, resetScroll: false });
        }
      }}
    />
    </>
  );
}
